import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@app/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Badge } from "@app/components/ui/badge";
import { fmtMs } from "@app/lib/format";
import { Trans } from "@lingui/macro";
import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface ExperimentData {
  id: string;
  name: string;
  status: string;
  created_at: string;
}

interface RunData {
  id: string;
  actual_output: Record<string, unknown> | null;
  latency_ms: number | null;
  total_tokens: number | null;
  status: string;
  feedbacks: Array<{
    key: string;
    score: number | null;
  }>;
}

interface PerExample {
  example_id: string;
  inputs: Record<string, unknown> | null;
  expected_outputs: Record<string, unknown> | null;
  runs: Record<string, RunData>;
}

interface AggregateMetrics {
  avg_latency_ms: number | null;
  avg_tokens: number | null;
  total_runs: number;
  failed_runs: number;
  success_rate: number | null;
  scores_by_key: Record<string, { avg_score: number; count: number }>;
  macro_f1?: number | null;
  weighted_f1?: number | null;
  exact_match_rate?: number | null;
  error_rate?: number | null;
  /**
   * What each mean is a mean OF. A Δ between two experiments describes the model only when both
   * were scored over the same fields on the same surface; the base is the clinicians' validation
   * coverage, which grows between two runs of the same dataset.
   */
  n_scored_fields?: number | null;
  n_unvalidated_fields?: number | null;
  unvalidated_fill_rate?: number | null;
  scored_surface?: string | null;
  n_total_merge_conflicts?: number | null;
  n_runs_with_conflicts?: number | null;
  latency_p50_ms?: number | null;
  latency_p95_ms?: number | null;
  total_wall_ms?: number | null;
}

interface PerFieldComparisonRow {
  field_key: string;
  name: string | null;
  group: string | null;
  evaluator_type: string | null;
  weight: number | null;
  by_experiment: Record<
    string,
    {
      mean_f1: number | null;
      mean_precision: number | null;
      mean_recall: number | null;
      exact_match_rate: number | null;
      n: number | null;
    }
  >;
}

interface PerGroupComparisonRow {
  group: string;
  n_fields: number | null;
  by_experiment: Record<
    string,
    {
      mean_f1: number | null;
      exact_match_rate: number | null;
      mean_latency_ms: number | null;
      sum_latency_ms: number | null;
    }
  >;
}

interface EvalComparison {
  per_field: PerFieldComparisonRow[];
  per_group: PerGroupComparisonRow[];
}

export interface ComparisonData {
  experiments: ExperimentData[];
  per_example: PerExample[];
  aggregate_metrics: Record<string, AggregateMetrics>;
  eval_comparison?: EvalComparison;
}

interface ExperimentComparisonViewProps {
  data: ComparisonData;
}

const COLORS = [
  "#1752B7",
  "#22c55e",
  "#3b82f6",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];

function truncateJson(obj: unknown, maxLen: number = 50): string {
  if (obj === null || obj === undefined) return "—";
  const str = typeof obj === "string" ? obj : JSON.stringify(obj, null, 0);
  if (str.length <= maxLen) return str;
  return str.substring(0, maxLen) + "...";
}

function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}


function scoreClass(score: number | null | undefined): string {
  if (score === null || score === undefined) return "text-slate-400";
  if (score >= 0.8) return "text-green-700";
  if (score >= 0.5) return "text-primary";
  return "text-red-600";
}

function DeltaCell({
  current,
  baseline,
  ascendingIsBetter = true,
  fmt = fmtPct,
}: {
  current: number | null | undefined;
  baseline: number | null | undefined;
  ascendingIsBetter?: boolean;
  fmt?: (v: number | null | undefined) => string;
}) {
  if (current === null || current === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  if (baseline === null || baseline === undefined || baseline === current) {
    return <span className="text-slate-700">{fmt(current)}</span>;
  }
  const delta = current - baseline;
  const positive = ascendingIsBetter ? delta > 0 : delta < 0;
  const Icon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : ArrowRight;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-slate-700">{fmt(current)}</span>
      <span
        className={`inline-flex items-center text-xs ${
          positive ? "text-green-700" : "text-red-600"
        }`}
      >
        <Icon className="h-3 w-3" />
        {fmt(Math.abs(delta) as number)}
      </span>
    </span>
  );
}

export function ExperimentComparisonView({
  data,
}: ExperimentComparisonViewProps) {
  const { experiments, per_example, aggregate_metrics, eval_comparison } = data;
  // First experiment in the URL order is treated as the baseline. Deltas in
  // the side-by-side tables are computed against this experiment's value.
  const baseline = experiments[0];

  // Build chart data for aggregate score comparison
  const allKeys = new Set<string>();
  Object.values(aggregate_metrics).forEach((m) => {
    Object.keys(m.scores_by_key).forEach((k) => allKeys.add(k));
  });

  const scoreChartData = Array.from(allKeys).map((key) => {
    const entry: Record<string, unknown> = { metric: key };
    experiments.forEach((exp) => {
      const score = aggregate_metrics[exp.id]?.scores_by_key?.[key]?.avg_score;
      entry[exp.name] = score !== undefined ? +(score * 100).toFixed(1) : 0;
    });
    return entry;
  });

  // Build latency/token comparison chart data
  const perfChartData = experiments.map((exp) => ({
    name: exp.name,
    "Latence (ms)": aggregate_metrics[exp.id]?.avg_latency_ms
      ? Math.round(aggregate_metrics[exp.id].avg_latency_ms!)
      : 0,
    "Tokens moy.": aggregate_metrics[exp.id]?.avg_tokens
      ? Math.round(aggregate_metrics[exp.id].avg_tokens!)
      : 0,
  }));

  // Build per-group F1 chart data (one bar per group, grouped by experiment).
  const perGroupChartData =
    eval_comparison?.per_group.map((row) => {
      const entry: Record<string, unknown> = { group: row.group };
      experiments.forEach((exp) => {
        const v = row.by_experiment?.[exp.id]?.mean_f1;
        entry[exp.name] = v !== null && v !== undefined ? +(v * 100).toFixed(1) : 0;
      });
      return entry;
    }) || [];

  const hasEvalMetrics = experiments.some(
    (exp) =>
      aggregate_metrics[exp.id]?.macro_f1 !== null &&
      aggregate_metrics[exp.id]?.macro_f1 !== undefined,
  );

  // Two conditions under which the Δ column below is not a statement about the model, and the
  // reader has no way to know it from the numbers alone.
  //
  // The surface: EXTRACTION re-runs the flat aggregation, PROJECTION replays the dated journal.
  // They are produced by different code and have been measured disagreeing on a real record, so
  // a delta across them compares two engines, not two configurations.
  //
  // The base: the score averages the fields a clinician answered for. That set grows as
  // validation continues, so the same dataset re-run a month later can move the F1 without the
  // extraction having changed at all.
  const surfaces = new Set(
    experiments.map(
      (exp) => aggregate_metrics[exp.id]?.scored_surface ?? "EXTRACTION",
    ),
  );
  const scoredCounts = experiments
    .map((exp) => aggregate_metrics[exp.id]?.n_scored_fields)
    .filter((n): n is number => typeof n === "number");
  const basesDiffer =
    scoredCounts.length > 1 &&
    Math.min(...scoredCounts) !== Math.max(...scoredCounts);

  return (
    <div className="space-y-6">
      {/* ── Evaluation against the validated truth ── */}
      {hasEvalMetrics && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              <Trans>Évaluation contre la vérité validée</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(surfaces.size > 1 || basesDiffer) && (
              <p className="text-xs text-slate-600 bg-blue-50 border border-slate-200 rounded-lg px-3 py-2 mb-3">
                {surfaces.size > 1 && (
                  <Trans>
                    Ces expériences ne mesurent pas la même surface (agrégation
                    à plat et projection datée) : l'écart compare deux moteurs,
                    pas deux configurations.
                  </Trans>
                )}{" "}
                {basesDiffer && (
                  <Trans>
                    Elles ne portent pas non plus sur le même nombre de champs
                    validés : le score moyenne les champs auxquels un médecin a
                    répondu, et cet ensemble grandit à mesure que la validation
                    avance — l'écart peut donc bouger sans que l'extraction ait
                    changé.
                  </Trans>
                )}
              </p>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead><Trans>Expérience</Trans></TableHead>
                  <TableHead><Trans>Macro F1</Trans></TableHead>
                  <TableHead><Trans>Champs notés</Trans></TableHead>
                  <TableHead><Trans>Weighted F1</Trans></TableHead>
                  <TableHead><Trans>Exact-match</Trans></TableHead>
                  <TableHead><Trans>Taux d'erreur</Trans></TableHead>
                  <TableHead><Trans>Conflits</Trans></TableHead>
                  <TableHead><Trans>Latence p50 / p95</Trans></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {experiments.map((exp, idx) => {
                  const m = aggregate_metrics[exp.id];
                  const base = aggregate_metrics[baseline.id];
                  return (
                    <TableRow key={exp.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: COLORS[idx % COLORS.length],
                            }}
                          />
                          <span className="font-medium">{exp.name}</span>
                          {idx === 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Trans>baseline</Trans>
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={scoreClass(m?.macro_f1)}>
                        <DeltaCell
                          current={m?.macro_f1}
                          baseline={idx === 0 ? null : base?.macro_f1}
                        />
                      </TableCell>
                      {/*
                        Printed raw, never as a Δ: a count of answered fields is not a score, and
                        a "+12" here would read as an improvement when it is a change of base.
                      */}
                      <TableCell className="text-slate-600">
                        {typeof m?.n_scored_fields === "number"
                          ? m.n_scored_fields
                          : "—"}
                      </TableCell>
                      <TableCell className={scoreClass(m?.weighted_f1)}>
                        <DeltaCell
                          current={m?.weighted_f1}
                          baseline={idx === 0 ? null : base?.weighted_f1}
                        />
                      </TableCell>
                      <TableCell className={scoreClass(m?.exact_match_rate)}>
                        <DeltaCell
                          current={m?.exact_match_rate}
                          baseline={idx === 0 ? null : base?.exact_match_rate}
                        />
                      </TableCell>
                      <TableCell>
                        <DeltaCell
                          current={m?.error_rate}
                          baseline={idx === 0 ? null : base?.error_rate}
                          ascendingIsBetter={false}
                        />
                      </TableCell>
                      <TableCell>
                        {m?.n_total_merge_conflicts ?? 0}
                        <span className="text-slate-400 text-xs ml-1">
                          ({m?.n_runs_with_conflicts ?? 0} <Trans>patient(s)</Trans>)
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {fmtMs(m?.latency_p50_ms)} / {fmtMs(m?.latency_p95_ms)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── Per-field side-by-side ────────────────────────────────────── */}
      {eval_comparison && eval_comparison.per_field.length > 0 && (
        <Card data-tour="compare-per-field">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              <Trans>Comparaison par champ (F1)</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3"><Trans>Groupe</Trans></th>
                  <th className="py-2 pr-3"><Trans>Champ</Trans></th>
                  {experiments.map((exp, idx) => (
                    <th key={exp.id} className="py-2 pr-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <span
                          className="w-2 h-2 rounded-full inline-block"
                          style={{
                            backgroundColor: COLORS[idx % COLORS.length],
                          }}
                        />
                        {exp.name}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {eval_comparison.per_field
                  .slice()
                  .sort((a, b) => {
                    // Surface the rows with the biggest spread between experiments
                    // first — those are where the comparison is informative.
                    const vals = (row: PerFieldComparisonRow) =>
                      experiments
                        .map((e) => row.by_experiment?.[e.id]?.mean_f1)
                        .filter(
                          (v): v is number => v !== null && v !== undefined,
                        );
                    const va = vals(a);
                    const vb = vals(b);
                    const spread = (xs: number[]) =>
                      xs.length < 2
                        ? 0
                        : Math.max(...xs) - Math.min(...xs);
                    return spread(vb) - spread(va);
                  })
                  .map((row, index) => (
                    <tr
                      key={row.field_key}
                      data-tour={index === 0 ? "compare-top-field" : undefined}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <td className="py-2 pr-3 text-slate-500">
                        {row.group || "—"}
                      </td>
                      <td className="py-2 pr-3 font-medium text-slate-800">
                        {row.name || row.field_key}
                      </td>
                      {experiments.map((exp, idx) => {
                        const cur = row.by_experiment?.[exp.id]?.mean_f1;
                        const base =
                          row.by_experiment?.[baseline.id]?.mean_f1;
                        return (
                          <td
                            key={exp.id}
                            className="py-2 pr-3 text-right"
                          >
                            <DeltaCell
                              current={cur}
                              baseline={idx === 0 ? null : base}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── Per-group F1 bar chart ─────────────────────────────────────── */}
      {perGroupChartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              <Trans>F1 par groupe</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(220, 35 * perGroupChartData.length)}>
              <BarChart
                data={perGroupChartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  unit="%"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                />
                <YAxis
                  dataKey="group"
                  type="category"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  width={120}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                  }}
                />
                <Legend />
                {experiments.map((exp, idx) => (
                  <Bar
                    key={exp.id}
                    dataKey={exp.name}
                    fill={COLORS[idx % COLORS.length]}
                    radius={[0, 4, 4, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Aggregate metrics table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Métriques agrégées</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Expérience</TableHead>
                <TableHead>Runs</TableHead>
                <TableHead>Taux réussite</TableHead>
                <TableHead>Latence moy.</TableHead>
                <TableHead>Tokens moy.</TableHead>
                {Array.from(allKeys).map((key) => (
                  <TableHead key={key} className="capitalize">
                    {key}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {experiments.map((exp, idx) => {
                const m = aggregate_metrics[exp.id];
                return (
                  <TableRow key={exp.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: COLORS[idx % COLORS.length],
                          }}
                        />
                        <span className="font-medium">{exp.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{m?.total_runs || 0}</TableCell>
                    <TableCell>
                      {m?.success_rate !== null && m?.success_rate !== undefined
                        ? `${(m.success_rate * 100).toFixed(0)}%`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {m?.avg_latency_ms
                        ? `${Math.round(m.avg_latency_ms)}ms`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {m?.avg_tokens ? Math.round(m.avg_tokens) : "—"}
                    </TableCell>
                    {Array.from(allKeys).map((key) => (
                      <TableCell key={key}>
                        {m?.scores_by_key?.[key] ? (
                          <Badge
                            variant={
                              m.scores_by_key[key].avg_score >= 0.7
                                ? "default"
                                : m.scores_by_key[key].avg_score >= 0.4
                                  ? "secondary"
                                  : "destructive"
                            }
                          >
                            {(m.scores_by_key[key].avg_score * 100).toFixed(1)}%
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Score comparison chart */}
      {scoreChartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Comparaison des scores par critère
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={scoreChartData}
                margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="metric"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                />
                <YAxis tick={{ fontSize: 12, fill: "#64748b" }} unit="%" />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                  }}
                />
                <Legend />
                {experiments.map((exp, idx) => (
                  <Bar
                    key={exp.id}
                    dataKey={exp.name}
                    fill={COLORS[idx % COLORS.length]}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Performance comparison chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={perfChartData}
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
              <Tooltip
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "12px",
                }}
              />
              <Legend />
              <Bar
                dataKey="Latence (ms)"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <Bar dataKey="Tokens moy." fill="#22c55e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Per-example comparison table */}
      {per_example.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Comparaison par exemple ({per_example.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white">
                      Entrée
                    </TableHead>
                    <TableHead>Attendu</TableHead>
                    {experiments.map((exp, idx) => (
                      <TableHead key={exp.id}>
                        <div className="flex items-center gap-1">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: COLORS[idx % COLORS.length],
                            }}
                          />
                          {exp.name}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {per_example.slice(0, 50).map((pe) => (
                    <TableRow key={pe.example_id}>
                      <TableCell className="font-mono text-xs max-w-[120px] sticky left-0 bg-white">
                        {truncateJson(pe.inputs)}
                      </TableCell>
                      <TableCell className="font-mono text-xs max-w-[120px]">
                        {truncateJson(pe.expected_outputs)}
                      </TableCell>
                      {experiments.map((exp) => {
                        const run = pe.runs[exp.id];
                        if (!run) {
                          return (
                            <TableCell key={exp.id} className="text-slate-400">
                              —
                            </TableCell>
                          );
                        }
                        const avgScore =
                          run.feedbacks.length > 0
                            ? run.feedbacks.reduce(
                                (sum, f) => sum + (f.score || 0),
                                0,
                              ) / run.feedbacks.length
                            : null;

                        return (
                          <TableCell
                            key={exp.id}
                            className="font-mono text-xs max-w-[150px]"
                          >
                            <div>{truncateJson(run.actual_output)}</div>
                            {avgScore !== null && (
                              <Badge
                                variant={
                                  avgScore >= 0.7
                                    ? "default"
                                    : avgScore >= 0.4
                                      ? "secondary"
                                      : "destructive"
                                }
                                className="mt-1 text-xs"
                              >
                                {(avgScore * 100).toFixed(0)}%
                              </Badge>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
